import { useTranslation } from 'react-i18next';
import { Navigate, Route } from 'react-router-dom';

import { AdminRoute, PayOutlet, PayRoutes } from 'components';
import { ROUTE_PATH as ROUTE_PATH_ALL } from 'constants/routes';
import {
  CommonArchivePage,
  PayinArchivePage,
  PayoutArchivePage,
} from 'features/archive';
import { BalanceReconciliationPage } from 'features/balance-reconciliation';
import {
  BlockedCustomerDetailsPage,
  BlockedCustomersPage,
} from 'features/blocked-customers';
import { CascadesList, ManageCascade } from 'features/cascades';
import { CommonLimitsPage } from 'features/common-limits';
import {
  CommonConfigsPage,
  PayinConfigsPage,
  PayoutConfigsPage,
} from 'features/configs';
import {
  CounterpartiesPage,
  CounterpartyDetails,
} from 'features/counterparties';
import { CurrencyExchangeRequestsPage } from 'features/currency-exchange-requests';
import { CustomersPage } from 'features/customers';
import { ExecutionMetricsPage } from 'features/execution-metrics';
import { ManageOrderAutomationPage } from 'features/order-automation';
import { P2PProviderPayinCustomerDailyLimitsPage } from 'features/p2p-provider-payin-customer-daily-limits';
import { P2PProviderWebhooksPage } from 'features/p2p-provider-webhooks';
import {
  P2PProviderDetailsPage,
  P2PProvidersTabs,
} from 'features/p2p-providers';
import { ManagePayoutOrderAutomationPage } from 'features/payout-order-automation';
import {
  PayoutRequisitesDetailsPage,
  PayoutRequisitesPage,
} from 'features/payout-requisites';
import {
  RequisitesCustomerLimitsPage,
  RequisitesDetailsPage,
  RequisitesGroupDetails,
  RequisitesPage,
} from 'features/requisites';
import { RequisitesDebugPage } from 'features/requisites-distribution';
import { ShopDetailsPage } from 'features/shop';
import { TelegramFlow } from 'features/telegram-flow';
import { TradeMethodDeeplinksPage } from 'features/trade-methods';
import {
  TrustedCustomerDetailsPage,
  TrustedCustomersPage,
} from 'features/trusted-customers';
import { TranslationNamespace } from 'i18n';
import {
  AdminMainPage,
  AdminSettingsPage,
  AdminUsersPage,
  AdminTradersPage,
  AdminShopsPage,
  AdminPayinCashbackPage,
  AdminPayoutCashbackPage,
  AdminTraderDetails,
  AdminUserDetailsPage,
  AdminFundsRequestsPage,
  AdminInternalTransfersPage,
  AdminAssetBalanceHistoryPage,
  AdminOrdersPage,
  AdminPayinOrdersPage,
  AdminPayoutOrdersPage,
  AdminGroupsPage,
  AdminGroupDetailsPage,
  AdminCurrenciesPage,
  AdminCurrencyExchangeDetailsPage,
  AdminBanksPage,
  AdminPayinStatisticsPage,
  AdminPayoutStatisticsPage,
  AdminTransactionsPage,
  AdminDepositTransactionsPage,
  AdminWithdrawalTransactionsPage,
  AdminTradeMethodsPage,
  AdminTradeMethodParsingsPage,
  AdminTradeMethodAutomationSourcesPage,
  AdminTradeMethodFilesValidationPage,
  AdminAnomaliesPage,
  AdminWebhooksPage,
  AdminAutomationSourcesPage,
  AdminSciDomainsPage,
  AdminWalletsPage,
  AdminAuditPage,
} from 'pages';

const ROUTE_PATH = ROUTE_PATH_ALL.ADMIN;

export const AdminRoutes = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.requisites.tabs',
  });

  return (
    <Route path={ROUTE_PATH.MAIN} element={<AdminRoute />}>
      <Route path="" index element={<AdminMainPage />} />
      <Route path={ROUTE_PATH.SETTINGS} element={<AdminSettingsPage />} />
      <Route path={ROUTE_PATH.USERS} element={<AdminUsersPage />} />
      <Route path={ROUTE_PATH.TRADERS} element={<AdminTradersPage />} />
      <Route path={ROUTE_PATH.SHOPS} element={<AdminShopsPage />} />

      <Route path={ROUTE_PATH.SHOP_DETAILS} element={<ShopDetailsPage />} />
      <Route
        path={`${ROUTE_PATH.REQUISITES}/*`}
        element={
          <PayOutlet
            payinTo={ROUTE_PATH.PAYIN_REQUISITES}
            payoutTo={ROUTE_PATH.PAYOUT_REQUISITES}
            commonTo={`${ROUTE_PATH.REQUISITES}/common`}
            extraLinks={[
              {
                label: t('debug'),
                to: `${ROUTE_PATH.REQUISITES_DEBUG}?tab=distribution_cache`,
                matchPathPattern: ROUTE_PATH.REQUISITES_DEBUG,
              },
            ]}
          />
        }
      >
        <Route
          index
          element={<Navigate to={ROUTE_PATH.PAYIN_REQUISITES} replace />}
        />
        <Route path="payin" element={<RequisitesPage />} />
        <Route path="payout" element={<PayoutRequisitesPage />} />
        <Route path="common" element={<CommonLimitsPage />} />
        <Route path="debug" element={<RequisitesDebugPage />} />
        <Route
          path="debug/cache"
          element={
            <Navigate
              to={`${ROUTE_PATH.REQUISITES_DEBUG}?tab=distribution_cache`}
              replace
            />
          }
        />
        <Route
          path="debug/requisites"
          element={
            <Navigate
              to={`${ROUTE_PATH.REQUISITES_DEBUG}?tab=requisites_debug`}
              replace
            />
          }
        />
        <Route
          path="*"
          element={<Navigate to={ROUTE_PATH.PAYIN_REQUISITES} replace />}
        />
      </Route>
      <Route
        path={ROUTE_PATH.PAYIN_REQUISITES_DETAILS}
        element={<RequisitesDetailsPage />}
      />
      <Route
        path={ROUTE_PATH.PAYOUT_REQUISITES_DETAILS}
        element={<PayoutRequisitesDetailsPage />}
      />
      <Route
        path={`${ROUTE_PATH.CASHBACK}/*`}
        element={
          <PayRoutes
            path={ROUTE_PATH.CASHBACK}
            payinElement={<AdminPayinCashbackPage />}
            payoutElement={<AdminPayoutCashbackPage />}
          />
        }
      />
      <Route
        path={ROUTE_PATH.DISTRIBUTION}
        element={
          <Navigate
            to={`${ROUTE_PATH.REQUISITES_DEBUG}?tab=distribution_cache`}
            replace
          />
        }
      />
      <Route
        path={ROUTE_PATH.DISTRIBUTION_DEBUG}
        element={
          <Navigate
            to={`${ROUTE_PATH.REQUISITES_DEBUG}?tab=requisites_debug`}
            replace
          />
        }
      />
      <Route
        path={ROUTE_PATH.PAYIN_REQUISITES_GROUP_DETAILS}
        element={<RequisitesGroupDetails />}
      />
      <Route
        path={ROUTE_PATH.PAYIN_REQUISITES_CUSTOMER_LIMITS}
        element={<RequisitesCustomerLimitsPage />}
      />

      <Route
        path={ROUTE_PATH.TRADER_DETAILS}
        element={<AdminTraderDetails />}
      />
      <Route
        path={ROUTE_PATH.USER_DETAILS}
        element={<AdminUserDetailsPage />}
      />
      <Route
        path={ROUTE_PATH.FUNDS_REQUESTS}
        element={<AdminFundsRequestsPage />}
      />
      <Route
        path={ROUTE_PATH.INTERNAL_TRANSFERS}
        element={<AdminInternalTransfersPage />}
      />
      <Route
        path={ROUTE_PATH.ASSET_BALANCE_HISTORY}
        element={<AdminAssetBalanceHistoryPage />}
      />
      <Route path={ROUTE_PATH.PAYIN_ORDERS} element={<AdminOrdersPage />} />

      <Route
        path={ROUTE_PATH.ORDERS}
        element={<Navigate to={ROUTE_PATH.PAYIN_ORDERS} />}
      />
      <Route path={ROUTE_PATH.ORDERS} element={<AdminOrdersPage />}>
        <Route
          index
          path={ROUTE_PATH.PAYIN_ORDERS}
          element={<AdminPayinOrdersPage />}
        />
        <Route
          path={ROUTE_PATH.PAYOUT_ORDERS}
          element={<AdminPayoutOrdersPage />}
        />
        <Route path="*" element={<Navigate to={ROUTE_PATH.PAYIN_ORDERS} />} />
      </Route>

      <Route path={ROUTE_PATH.GROUPS} element={<AdminGroupsPage />} />
      <Route
        path={ROUTE_PATH.GROUP_DETAILS}
        element={<AdminGroupDetailsPage />}
      />
      <Route path={ROUTE_PATH.CURRENCIES} element={<AdminCurrenciesPage />} />
      <Route
        path={ROUTE_PATH.CURRENCY_EXCHANGE_DETAILS}
        element={<AdminCurrencyExchangeDetailsPage />}
      />
      <Route path={ROUTE_PATH.BANKS} element={<AdminBanksPage />} />

      <Route
        path={`${ROUTE_PATH.STATISTICS}/*`}
        element={
          <PayRoutes
            path={ROUTE_PATH.STATISTICS}
            payinElement={<AdminPayinStatisticsPage />}
            payoutElement={<AdminPayoutStatisticsPage />}
          />
        }
      />
      <Route
        path={ROUTE_PATH.TRANSACTIONS}
        element={<Navigate to={ROUTE_PATH.DEPOSIT_TRANSACTIONS} />}
      />
      <Route path={ROUTE_PATH.TRANSACTIONS} element={<AdminTransactionsPage />}>
        <Route
          index
          path={ROUTE_PATH.DEPOSIT_TRANSACTIONS}
          element={<AdminDepositTransactionsPage />}
        />
        <Route
          path={ROUTE_PATH.WITHDRAWAL_TRANSACTIONS}
          element={<AdminWithdrawalTransactionsPage />}
        />
        <Route
          path="*"
          element={<Navigate to={ROUTE_PATH.DEPOSIT_TRANSACTIONS} />}
        />
      </Route>

      <Route path={ROUTE_PATH.TRADE_METHODS}>
        <Route
          index
          path={ROUTE_PATH.TRADE_METHODS}
          element={<AdminTradeMethodsPage />}
        />
        <Route
          path={ROUTE_PATH.TRADE_METHOD_PARSINGS}
          element={<AdminTradeMethodParsingsPage />}
        />
        <Route
          path={ROUTE_PATH.TRADE_METHOD_AUTOMATION_SOURCES}
          element={<AdminTradeMethodAutomationSourcesPage />}
        />
        <Route
          path={ROUTE_PATH.TRADE_METHOD_FILES_VALIDATION}
          element={<AdminTradeMethodFilesValidationPage />}
        />
        <Route
          path={ROUTE_PATH.TRADE_METHOD_DEEPLINKS}
          element={<TradeMethodDeeplinksPage />}
        />
      </Route>
      <Route path={ROUTE_PATH.ANOMALIES} element={<AdminAnomaliesPage />} />
      <Route
        path={`${ROUTE_PATH.ORDER_AUTOMATION}/*`}
        element={
          <PayRoutes
            path={ROUTE_PATH.ORDER_AUTOMATION}
            payinElement={<ManageOrderAutomationPage />}
            payoutElement={<ManagePayoutOrderAutomationPage />}
          />
        }
      ></Route>
      <Route
        path={`${ROUTE_PATH.ARCHIVE}/*`}
        element={
          <PayRoutes
            path={ROUTE_PATH.ARCHIVE}
            payinElement={<PayinArchivePage />}
            payoutElement={<PayoutArchivePage />}
            commonElement={<CommonArchivePage />}
          />
        }
      ></Route>
      <Route path={ROUTE_PATH.WEBHOOKS} element={<AdminWebhooksPage />} />
      <Route
        path={ROUTE_PATH.AUTOMATION_SOURCES}
        element={<AdminAutomationSourcesPage />}
      />
      <Route path={ROUTE_PATH.SCI_DOMAINS} element={<AdminSciDomainsPage />} />
      <Route path={ROUTE_PATH.WALLETS} element={<AdminWalletsPage />} />
      <Route path={ROUTE_PATH.AUDIT} element={<AdminAuditPage />} />
      <Route
        path={ROUTE_PATH.BALANCE_RECONCILIATION}
        element={<BalanceReconciliationPage />}
      />
      <Route path={ROUTE_PATH.P2P_PROVIDERS} element={<P2PProvidersTabs />} />
      <Route
        path={ROUTE_PATH.P2P_PROVIDERS_DETAILS}
        element={<P2PProviderDetailsPage />}
      />
      <Route
        path={ROUTE_PATH.P2P_PROVIDER_WEBHOOKS}
        element={<P2PProviderWebhooksPage />}
      />
      <Route
        path={ROUTE_PATH.P2P_PROVIDER_PAYIN_CUSTOMER_DAILY_LIMITS}
        element={<P2PProviderPayinCustomerDailyLimitsPage />}
      />
      <Route
        path={ROUTE_PATH.CURRENCY_EXCHANGE}
        element={<CurrencyExchangeRequestsPage />}
      />
      <Route
        path={ROUTE_PATH.CUSTOMERS}
        element={<Navigate to={ROUTE_PATH.BLOCKED_CUSTOMERS} />}
      />
      <Route path={ROUTE_PATH.CUSTOMERS} element={<CustomersPage />}>
        <Route
          index
          path={ROUTE_PATH.BLOCKED_CUSTOMERS}
          element={<BlockedCustomersPage />}
        />
        <Route
          path={ROUTE_PATH.TRUSTED_CUSTOMERS}
          element={<TrustedCustomersPage />}
        />
        <Route
          path={ROUTE_PATH.COUNTERPARTIES}
          element={<CounterpartiesPage />}
        />
        <Route
          path="*"
          element={<Navigate to={ROUTE_PATH.BLOCKED_CUSTOMERS} />}
        />
      </Route>
      <Route
        path={ROUTE_PATH.BLOCKED_CUSTOMERS_DETAILS}
        element={<BlockedCustomerDetailsPage />}
      />
      <Route
        path={ROUTE_PATH.TRUSTED_CUSTOMERS_DETAILS}
        element={<TrustedCustomerDetailsPage />}
      />
      <Route
        path={ROUTE_PATH.COUNTERPARTY_DETAILS}
        element={<CounterpartyDetails />}
      />
      <Route
        path={`${ROUTE_PATH.CONFIGS}/*`}
        element={
          <PayRoutes
            path={ROUTE_PATH.CONFIGS}
            payinElement={<PayinConfigsPage />}
            payoutElement={<PayoutConfigsPage />}
            commonElement={<CommonConfigsPage />}
          />
        }
      ></Route>
      <Route path={ROUTE_PATH.CASCADES} element={<CascadesList />} />
      <Route path={ROUTE_PATH.CASCADE_DETAILS} element={<ManageCascade />} />
      <Route
        path={ROUTE_PATH.EXECUTION_METRICS}
        element={<ExecutionMetricsPage />}
      />
      <Route path={ROUTE_PATH.TELEGRAM_FLOW} element={<TelegramFlow />} />
    </Route>
  );
};
